import { Channel } from 'pusher-js';
import { showReportFailedToast, showReportSuccessToast } from 'src/components/Toast';
import { apolloClient } from 'src/graphql/client';
import { ExportStatus } from 'src/graphql/_generated';
import { navigateGlobal } from 'src/utils/navigation';
import { captureMessage } from '@sentry/react';
import { realtimeLog } from 'src/utils/logger';
import { getPusher, onPusherReconnect, unsubscribeChannel } from './pusher';

// Per-job export progress over the shared Reverb singleton. One export = one private channel.

interface ExportProgressEvent {
  uxId: string;
  status: string;
  progress: number;
}

interface ExportCompletedEvent {
  uxId: string;
  status: string;
  errorMessage: string | null;
}

const activeSubscriptions = new Map<string, Channel>();

// Events during a drop aren't buffered — re-fetch the job list on reconnect.
onPusherReconnect(() => {
  apolloClient.refetchQueries({ include: ['myExportJobs'] }).catch(() => {});
});

function notifyTerminal(e: ExportCompletedEvent): void {
  const status = e.status.toLowerCase();
  if (status === 'completed') {
    showReportSuccessToast({
      onCtaClick: () => navigateGlobal('/report-centre'),
    });
    return;
  }
  if (status === 'failed' || status === 'canceled') {
    showReportFailedToast(e.errorMessage ? { subtitle: e.errorMessage } : undefined);
  }
}

function writeStatus(uxId: string, status: string, extras: Record<string, unknown> = {}): void {
  const enumStatus = status.toUpperCase() as ExportStatus;
  apolloClient.cache.modify({
    id: apolloClient.cache.identify({ __typename: 'ExportJob', id: uxId }),
    fields: {
      status: () => enumStatus,
      ...Object.fromEntries(Object.entries(extras).map(([k, v]) => [k, () => v])),
    },
  });
}

/** Subscribe to live export progress (idempotent per uxId); auto-tears down on export.completed. */
export function subscribeToExportJob(uxId: string): () => void {
  if (activeSubscriptions.has(uxId)) {
    return () => unsubscribe(uxId);
  }

  const pusher = getPusher();
  if (!pusher) return () => {};

  const channelName = `private-export.${uxId}`;
  const channel = pusher.subscribe(channelName);
  activeSubscriptions.set(uxId, channel);

  channel.bind('pusher:subscription_error', (err: { status?: number } | unknown) => {
    const status = (err as { status?: number })?.status;
    realtimeLog.warn(`subscribe failed for ${channelName}`, status ?? err);
    captureMessage('export socket subscription failed', {
      level: 'warning',
      tags: { context: 'export_socket', http_status: String(status ?? 'unknown') },
      extra: { channel: channelName }, // private-export.<uxId> — an id, not PII
    });
    unsubscribe(uxId);
  });

  channel.bind('export.progress', (e: ExportProgressEvent) => {
    writeStatus(e.uxId, e.status, { progress: e.progress });
  });

  channel.bind('export.completed', (e: ExportCompletedEvent) => {
    writeStatus(e.uxId, e.status, { errorMessage: e.errorMessage });
    // The WS payload omits terminal-state timestamps (completedAt/failedAt/canceledAt) — refetch for them.
    apolloClient.refetchQueries({ include: ['myExportJobs'] }).catch(() => {});
    notifyTerminal(e);
    unsubscribe(uxId);
  });

  return () => unsubscribe(uxId);
}

function unsubscribe(uxId: string): void {
  if (!activeSubscriptions.has(uxId)) return;
  activeSubscriptions.delete(uxId);
  unsubscribeChannel(`private-export.${uxId}`);
}
